import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import NextLink from 'next/link';
import type {StaticImageData} from 'next/image';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import DisplayImage from '@/components/Ui-components/display-image';
import {borderRadius, colors, fontSize, fontWeight, gradients, shadows} from '@/styles/tokens';
import {ICON_TILE_ICON_SIZE, iconTileImageBaseStyle, iconTileImageStyle, iconTileSx as sharedIconTileSx} from '@/styles/iconTile';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import TokenOutlinedIcon from '@mui/icons-material/TokenOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import IntegrationInstructionsOutlinedIcon from '@mui/icons-material/IntegrationInstructionsOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

export interface CoreServiceItem {
    /** A static image (rendered + auto-sized by this component) or a custom node. */
    icon: StaticImageData | React.ReactNode;
    title: string;
    /** HTML string — supports inline <a> links. */
    description: string;
    redirect: string;
}

interface CoreServicesProps {
    heading?: string;
    /** Omit to render heading only (no paragraph). */
    subheading?: string;
    services?: CoreServiceItem[];
    headingAlign?: 'center' | 'left';
    iconVariant?: 'orange' | 'navy';
    /** Inline link label under each card (ignored when linkCard is true). */
    exploreLabel?: string;
    /** When true the whole card is a link to `redirect` and the inline link is hidden. */
    linkCard?: boolean;
    /** Fill the card with the navy gradient on hover (copy flips to white, icon tile inverts). */
    darkHover?: boolean;
}

// ─── Default content (about-us "Our core services") ──────────────────────────
const DEFAULT_HEADING = 'Our Core Services';
const DEFAULT_SUBHEADING =
    'We help businesses turn ideas into digital products that solve real challenges and support growth. From AI and blockchain to web and mobile development, our services are designed around your business needs and long-term goals.';

const DEFAULT_SERVICES: CoreServiceItem[] = [
    {
        icon: <AccountTreeOutlinedIcon />,
        title: 'Blockchain Development',
        redirect: 'https://www.debutinfotech.com/blockchain-development-services',
        description:
            'We build blockchain solutions that help businesses improve transaction transparency and bring greater trust to digital operations.'
    },
    {
        icon: <PsychologyOutlinedIcon />,
        title: 'AI/ML Development',
        redirect: 'https://www.debutinfotech.com/ai-development-company',
        description:
            'Turn business data into useful insights and automate repetitive work with AI solutions designed to improve efficiency and support better decisions.'
    },
    {
        icon: <LanguageOutlinedIcon />,
        title: 'Web Development',
        redirect: 'https://www.debutinfotech.com/web-development',
        description:
            'Our web development services help businesses build reliable digital platforms that improve customer experiences and support changing business needs.'
    },
    {
        icon: <PhoneAndroidOutlinedIcon />,
        title: 'Mobile App Development',
        redirect: 'https://www.debutinfotech.com/mobile-application-development-services',
        description:
            'We create mobile applications that make it easier for customers to engage with your business and access services on the go.'
    },
    {
        icon: <AccountBalanceWalletOutlinedIcon />,
        title: 'Crypto Wallet & Exchange Development',
        redirect: 'https://www.debutinfotech.com/cryptocurrency-exchange-development',
        description:
            'Launch secure <a href="https://www.debutinfotech.com/crypto-wallet" target="_blank">digital asset platforms</a> that give users a reliable way to manage assets and participate in <a href="https://www.debutinfotech.com/cryptocurrency-exchange-development" target="_blank">cryptocurrency markets</a>.'
    },
    {
        icon: <TokenOutlinedIcon />,
        title: 'Asset Tokenization Development',
        redirect: 'https://www.debutinfotech.com/real-world-asset-tokenization',
        description:
            'Our tokenization solutions help businesses represent real-world assets digitally and explore new ways to manage ownership and investment opportunities.'
    },
    {
        icon: <SmartToyOutlinedIcon />,
        title: 'AI Agent Development',
        redirect: 'https://www.debutinfotech.com/ai-agent-development-company',
        description: 'We develop AI agents that handle multi-step tasks and help businesses reduce manual effort across everyday workflows.'
    },
    {
        icon: <IntegrationInstructionsOutlinedIcon />,
        title: 'DevOps Consulting & Development',
        redirect: 'https://www.debutinfotech.com/devops',
        description:
            'Improve release reliability and accelerate software delivery with DevOps practices designed to support smoother deployments and more efficient engineering teams.'
    }
];

const isStaticImage = (icon: StaticImageData | React.ReactNode): icon is StaticImageData =>
    typeof icon === 'object' && icon !== null && 'src' in icon;

const iconTileSx = (variant: 'orange' | 'navy') =>
    ({
        ...sharedIconTileSx,
        background: variant === 'navy' ? gradients.darkSection : colors.background.orangeTint,
        color: variant === 'navy' ? colors.text.inverse : colors.accent.orange500
    } as const);

// Icons are flat monochrome images, so they are recoloured with a filter chain rather than a fill/mask.
// `brightness(0) saturate(100%)` flattens the source to black, and the rest tints it to colors.primary.deep.
const iconBlueFilter =
    'brightness(0) saturate(100%) invert(43.5%) sepia(99.3%) saturate(486.3%) hue-rotate(185deg) brightness(48%) contrast(106.2%)';

/** Navy fill on hover — the per-element colors are set inline, so they need !important to lose. */
const darkHoverSx = {
    backgroundImage: gradients.navyCardDeep,
    borderColor: 'transparent',
    '& h3, & p, & p a, & .font-headers, & .font-regular-text': {
        color: `${colors.text.inverse} !important`
    },
    '& .core-service-icon-tile': {
        background: colors.background.default,
        color: colors.primary.deep
    },
    '& .core-service-icon': {
        filter: `${iconBlueFilter} !important`
    }
} as const;

const cardSx = {
    p: {xs: 2, md: 2.5},
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
    height: '100%',
    backgroundColor: colors.background.default,
    borderRadius: borderRadius.xl,
    border: `1px solid ${colors.neutral.borderLight}`,
    textDecoration: 'none',
    backgroundImage: 'none',
    transition: 'transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease, border-color 0.25s ease',
    '&:hover': {transform: 'translateY(-6px)', boxShadow: shadows.lg}
} as const;

const CoreServices = ({
    heading = DEFAULT_HEADING,
    subheading = DEFAULT_SUBHEADING,
    services = DEFAULT_SERVICES,
    headingAlign = 'center',
    iconVariant = 'orange',
    exploreLabel = 'Explore Service',
    linkCard = false,
    darkHover = false
}: CoreServicesProps) => {
    const isCentered = headingAlign === 'center';

    return (
        <Box component="section">
            <MUIContainer>
                <Box>
                    {/* Header */}
                    <Box
                        textAlign={headingAlign}
                        maxWidth={isCentered ? 1024 : undefined}
                        margin={isCentered ? '0 auto' : undefined}
                        mb={5}
                    >
                        <Typography
                            variant="h1"
                            component="h2"
                            mb={subheading ? 3 : 0}
                            fontSize={{xs: fontSize[32], sm: fontSize[48]}}
                            className="font-headers"
                        >
                            {heading}
                        </Typography>
                        {subheading && (
                            <Typography component="p" className="font-regular-text" color={colors.text.primary}>
                                {subheading}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)'},
                        gap: {xs: 2, md: 3},
                        mt: 2
                    }}
                >
                    {services.map((service) => {
                        const cardProps = linkCard
                            ? {
                                  component: NextLink,
                                  href: service.redirect || '#',
                                  className: 'remove-underline',
                                  target: '_blank',
                                  rel: 'noopener noreferrer'
                              }
                            : {};
                        return (
                            <Box key={service.title} sx={darkHover ? [cardSx, {'&:hover': darkHoverSx}] : cardSx} {...cardProps}>
                                {/* Icon */}
                                <Box className="core-service-icon-tile" sx={iconTileSx(iconVariant)}>
                                    {isStaticImage(service.icon) ? (
                                        <DisplayImage
                                            src={service.icon}
                                            alt=""
                                            className="core-service-icon"
                                            width={ICON_TILE_ICON_SIZE}
                                            height={ICON_TILE_ICON_SIZE}
                                            style={iconVariant === 'navy' ? iconTileImageStyle : iconTileImageBaseStyle}
                                        />
                                    ) : (
                                        service.icon
                                    )}
                                </Box>

                                <Typography
                                    component="h3"
                                    className="font-headers"
                                    sx={{fontSize: fontSize[16], fontWeight: fontWeight.bold, color: colors.text.primary}}
                                >
                                    {service.title}
                                </Typography>

                                <Typography
                                    className="font-regular-text"
                                    sx={{
                                        fontSize: fontSize[16],
                                        color: colors.text.primary,
                                        lineHeight: 1.6,
                                        '& a': {textDecoration: 'underline'}
                                    }}
                                    dangerouslySetInnerHTML={{__html: service.description}}
                                />

                                {!linkCard && (
                                    <Box sx={{mt: 'auto'}}>
                                        <Link
                                            href={service.redirect || '#'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="none"
                                            className="font-headers"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.75,
                                                fontSize: fontSize[16],
                                                fontWeight: fontWeight.semibold,
                                                color: colors.accent.orange500,
                                                '& svg': {fontSize: fontSize[16]}
                                            }}
                                        >
                                            {exploreLabel}
                                            <ArrowForwardIcon />
                                        </Link>
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(CoreServices);
